import React, {useState, useEffect, useMemo} from 'react'
import axios from 'axios'
import {
  getMyConsultantSurveyResultData,
  getSurveyResultGeneralData,
} from '../survey/list/core/_requests'
import {useLocation} from 'react-router-dom'
import SurveyResultsComponent from '../../modules/surveyanalytics/surveyanalytics'
import SurveyTableComponent from '../../modules/surveyanalytics/surveyanalyticstable'
import SurveyGaugeChart from '../../modules/surveyanalytics/SurveyGaugeChart'
import MatrixQuestionChartSwitcher from '../../modules/surveyanalytics/MatrixQuestionChartSwitcher'
import {
  buildSurveyAiPayload,
  buildSurveyReportSummary,
} from '../../modules/surveyanalytics/surveyReportUtils'
import type {SurveyReportQuestionSummary} from '../../modules/surveyanalytics/surveyReportUtils'
import {downloadSurveyReportPdf} from '../../modules/surveyanalytics/surveyReportPdf'
import {sanitizeChatGptResponse} from '../../modules/surveyanalytics/utils'

const API_URL = process.env.REACT_APP_API_URL
const isAiActive = process.env.REACT_APP_YZACTIVE === '1'

// Yükleme göstergesi için bir CSS sınıfı ekleyin
const spinnerStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100vh',
  fontSize: '2rem',
  color: '#555',
}

const SurveyResultGeneral = () => {
  const location = useLocation()
  const state: any = location.state

  const [surveyResults, setSurveyResults] = React.useState<any[]>([])
  const [survey, setSurvey] = React.useState<any>('')
  const [surveyGuid, setSurveyGuid] = React.useState<string>('')
  const [surveyName, setSurveyName] = React.useState<string>('')
  const [semesterName, setSemesterName] = React.useState<string>('')
  const [finished, setFinished] = React.useState(0)
  const [showMatrixCharts, setShowMatrixCharts] = useState(false)
  const [generatedAt] = useState<string>(() => new Date().toLocaleString('tr-TR'))
  const [surveyEndAt, setSurveyEndAt] = React.useState<string | null>(null)
  const [uniqueParticipantCount, setUniqueParticipantCount] = React.useState<number>(0)
  const [aiAnalysis, setAiAnalysis] = React.useState<string | null>(null)
  const [isLoadingAiAnalysis, setIsLoadingAiAnalysis] = React.useState(false)
  const [isDownloadingPdf, setIsDownloadingPdf] = React.useState(false)

  const formatSurveyDate = (value?: string | null) => {
    if (!value) return '-'

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return '-'

    return date.toLocaleDateString('tr-TR')
  }

  const surveyRequest = useMemo(() => {
    if (!state) return null

    if (typeof state === 'object') {
      return {
        guid: state.guid || state.Guid || '',
        surveyId: state.surveyId || state.id || state.Id,
        scope: state.scope,
      }
    }

    return {guid: state, surveyId: undefined, scope: undefined}
  }, [state])

  const reportSummary = useMemo(
    () =>
      buildSurveyReportSummary({
        surveyJson: survey,
        surveyData: surveyResults,
        surveyName,
        semesterName,
        generatedAt,
        surveyEndAt: formatSurveyDate(surveyEndAt),
        uniqueParticipantCount,
      }),
    [generatedAt, semesterName, survey, surveyEndAt, surveyName, surveyResults, uniqueParticipantCount]
  )

  const getOpenEndedAnswers = (item: SurveyReportQuestionSummary) =>
    item.answers && item.answers.length > 0 ? item.answers : item.sampleAnswers || []

  const openEndedAnswerCount = reportSummary.openEndedItems.reduce(
    (sum, item) => sum + getOpenEndedAnswers(item).length,
    0
  )

  const getQuestionTitle = (item: SurveyReportQuestionSummary) =>
    item.rowTitle ? `${item.questionTitle} - ${item.rowTitle}` : item.questionTitle

  const formatScore = (value?: number | null) =>
    typeof value === 'number' && Number.isFinite(value) ? value.toFixed(1) : '-'

  const formatPercent = (value?: number | null) =>
    typeof value === 'number' && Number.isFinite(value) ? `${value.toFixed(1)}%` : '-'

  const convertAnalysisToHtml = (text: string) =>
    text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => `<p class="mb-2">${line}</p>`)
      .join('')

  const downloadCsvReport = () => {
    if (reportSummary.likertItems.length === 0 && reportSummary.choiceItems.length === 0) return

    const rows = [...reportSummary.likertItems, ...reportSummary.choiceItems].flatMap((item) =>
      item.options.map((option) =>
        [
          getQuestionTitle(item),
          option.label,
          option.count,
          option.percentage.toFixed(2),
          item.answerCount,
          formatScore(item.normalizedAverage),
        ]
          .map((value) => `"${String(value).replace(/"/g, '""')}"`)
          .join(',')
      )
    )

    const csvContent = ['Soru,Seçenek,Sayı,Yüzde,Toplam Cevap,Ortalama', ...rows].join('\n')

    const blob = new Blob([`\uFEFF${csvContent}`], {type: 'text/csv;charset=utf-8;'})
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `anket-sonuc-frekans-${new Date().getTime()}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  const downloadOpenEndedCsv = () => {
    if (openEndedAnswerCount === 0) return

    const rows = reportSummary.openEndedItems.flatMap((item) =>
      getOpenEndedAnswers(item).map((answer, index) =>
        [getQuestionTitle(item), index + 1, answer]
          .map((value) => `"${String(value).replace(/"/g, '""')}"`)
          .join(',')
      )
    )

    const csvContent = ['Soru,Yanıt No,Yanıt', ...rows].join('\n')
    const blob = new Blob([`\uFEFF${csvContent}`], {type: 'text/csv;charset=utf-8;'})
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `anket-acik-uclu-yanitlar-${new Date().getTime()}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  const handleDownloadPdf = async () => {
    if (!reportSummary.responseCount) return

    setIsDownloadingPdf(true)
    try {
      await downloadSurveyReportPdf({summary: reportSummary, aiAnalysis})
    } catch (error) {
      console.error('PDF raporu oluşturulamadı:', error)
      window.alert('PDF raporu oluşturulamadı. Lütfen daha sonra tekrar deneyiniz.')
    } finally {
      setIsDownloadingPdf(false)
    }
  }

  const handleRunAiAnalysis = async () => {
    if (!isAiActive || !surveyGuid || !reportSummary.responseCount) return

    setIsLoadingAiAnalysis(true)
    try {
      const payload = {
        guid: surveyGuid,
        ...buildSurveyAiPayload(reportSummary),
      }
      const response = await axios.post(`${API_URL}/Custom/RunGeneralSurveyChatGptAnalysis`, payload)
      setAiAnalysis(sanitizeChatGptResponse(response?.data?.response))
    } catch (error) {
      console.error('Anket AI yorumu oluşturulamadı:', error)
      window.alert('Yapay zeka yorumu oluşturulamadı. Lütfen daha sonra tekrar deneyiniz.')
    } finally {
      setIsLoadingAiAnalysis(false)
    }
  }

  useEffect(() => {
    if (surveyRequest) {
      const request =
        surveyRequest.scope === 'consultant' && surveyRequest.surveyId
          ? getMyConsultantSurveyResultData(surveyRequest.surveyId)
          : getSurveyResultGeneralData(surveyRequest)

      request.then((surveyResponse: any) => {
        const surveyData = surveyResponse?.data
        const rawSurvey = surveyData?.surveyJson
        const rawSurveyName = surveyData?.surveyTypeName
        const rawSemesterName = surveyData?.semesterName
        const rawSurveyEndAt = surveyData?.surveyEndAt
        const rawUniqueParticipantCount = surveyData?.uniqueParticipantCount
        let parsedSurvey: any = ''

        try {
          parsedSurvey = rawSurvey ? JSON.parse(rawSurvey) : ''
        } catch (error) {
          parsedSurvey = ''
          console.error('Anket şablonu çözümlenemedi:', error)
        }

        const applicationArray = Array.isArray(surveyData?.surveyApplicationName)
          ? surveyData.surveyApplicationName
          : []

        const sanitizedResults = applicationArray
          .filter((entry: any) => typeof entry === 'string' && entry.length > 0)
          .map((entry: string) => {
            try {
              return JSON.parse(entry)
            } catch (error) {
              console.error('Anket sonucu çözümlenemedi:', error)
              return null
            }
          })
          .filter((entry: any) => {
            if (!entry || typeof entry !== 'object') {
              return false
            }

            return Object.values(entry).some((value: any) => {
              if (typeof value === 'string') {
                return value.trim().length > 0
              }

              if (Array.isArray(value)) {
                return value.length > 0
              }

              if (value && typeof value === 'object') {
                return Object.keys(value).length > 0
              }

              return false
            })
          })

        setSurvey(parsedSurvey)
        setSurveyGuid(surveyData?.guid || surveyRequest.guid || '')
        setSurveyName(rawSurveyName || parsedSurvey?.title || 'İsimsiz Anket')
        setSemesterName(rawSemesterName || 'Dönem bilgisi yok')
        setSurveyEndAt(rawSurveyEndAt || null)
        setAiAnalysis(null)
        setUniqueParticipantCount(
          typeof rawUniqueParticipantCount === 'number' ? rawUniqueParticipantCount : sanitizedResults.length
        )
        setSurveyResults(sanitizedResults)
      }).catch((error) => {
        console.error('Anket sonuç verileri alınamadı:', error)
        setSurvey('')
        setSurveyGuid('')
        setSurveyResults([])
        setSurveyEndAt(null)
        setAiAnalysis(null)
        setUniqueParticipantCount(0)
      })
      //console.log(state)
    }
  }, [surveyRequest])

  useEffect(() => {
    //console.log(surveyResults)
    //console.log(survey)
    if (surveyResults.length > 0 && survey !== '') {
      setFinished(1)
    }
  }, [surveyResults, survey])

  return (
    <>
      {finished === 0 && (
        <div style={spinnerStyle}>
          <i className='fa fa-spinner fa-spin' aria-hidden='true'></i>
        </div>
      )}
      {finished === 1 && surveyResults && (
        <>
          <style>{`
                      @media print {
                        @page {
                          size: A4 landscape;
                          margin: 8mm;
                        }
                        .survey-result-actions {
                          display: none !important;
                        }
                        .survey-print-title {
                          display: block !important;
                        }
                        .card,
                        table,
                        #surveyAnalyticsContainer,
                        #surveyTableContainer,
                        #surveyAnalyticsContainer *,
                        #surveyTableContainer * {
                          break-inside: avoid;
                          page-break-inside: avoid;
                        }
                        .sa-question,
                        .sa-question__content,
                        .sa-visualizer,
                        .svc-creator__content-holder,
                        .tabulator-row,
                        .tabulator-tableholder {
                          break-inside: avoid !important;
                          page-break-inside: avoid !important;
                        }
                        .matrix-question-group,
                        .matrix-chart-card {
                          break-inside: avoid !important;
                          page-break-inside: avoid !important;
                        }
                        .matrix-chart-actions {
                          display: none !important;
                        }
                        .matrix-chart-canvas {
                          height: 220px !important;
                        }
                        .matrix-chart-canvas canvas {
                          max-height: 220px !important;
                        }
                      }
                    `}</style>
          <div className='card mb-5 border-0 bg-white survey-print-title'>
            <div className='card-body py-3 text-center'>
              <h4 className='mb-0'>{surveyName}</h4>
            </div>
          </div>
          <div className='card mb-5 border-0 bg-light'>
            <div className='card-body py-4'>
              <h5 className='mb-3'>Anket Sonuç Raporu - {surveyName}</h5>
              <ul className='mb-0'>
                <li>
                  <strong>Anket Tarihi:</strong> {formatSurveyDate(surveyEndAt)}
                </li>
                <li>
                  <strong>Rapor Tarihi:</strong> {generatedAt}
                </li>
                <li>
                  <strong>Dönem:</strong> {semesterName}
                </li>
                <li>
                  <strong>Tamamlanan Anket Sayısı:</strong> {surveyResults.length}
                </li>
                <li>
                  <strong>Tekil Katılımcı Sayısı:</strong> {uniqueParticipantCount}
                </li>
                <li>
                  <strong>Rapor İçeriği:</strong> Grafikler, kırılım analizleri ve tablo
                </li>
              </ul>
            </div>
          </div>
          <div className='d-flex justify-content-end flex-wrap gap-2 mb-5 survey-result-actions'>
            <button
              className='btn btn-sm btn-light-primary'
              onClick={() => setShowMatrixCharts((prev) => !prev)}
            >
              {showMatrixCharts
                ? 'SurveyJS Grafiklerini Göster'
                : 'Pasta / Bar Grafiklerini Göster'}
            </button>
            <button
              className='btn btn-sm btn-light-success'
              onClick={() => {
                const payload = {
                  generatedAt,
                  surveyEndAt,
                  uniqueParticipantCount,
                  responseCount: surveyResults.length,
                  surveyJson: survey,
                  surveyResults,
                }
                const blob = new Blob([JSON.stringify(payload, null, 2)], {
                  type: 'application/json;charset=utf-8',
                })
                const url = URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = url
                link.download = `anket-sonuc-raporu-${new Date().getTime()}.json`
                link.click()
                URL.revokeObjectURL(url)
              }}
            >
              JSON İndir
            </button>
            <button className='btn btn-sm btn-light-info' onClick={downloadCsvReport}>
              CSV Frekans İndir
            </button>
            <button
              className='btn btn-sm btn-light-secondary'
              onClick={downloadOpenEndedCsv}
              disabled={openEndedAnswerCount === 0}
            >
              Açık Uçlu CSV İndir
            </button>
            {isAiActive && (
              <button
                className='btn btn-sm btn-light-warning'
                onClick={handleRunAiAnalysis}
                disabled={isLoadingAiAnalysis || !reportSummary.responseCount}
              >
                {isLoadingAiAnalysis ? 'Yorum Hazırlanıyor...' : 'Yapay Zeka Yorumu Oluştur'}
              </button>
            )}
            <button
              className='btn btn-sm btn-primary'
              onClick={handleDownloadPdf}
              disabled={isDownloadingPdf || !reportSummary.responseCount}
            >
              {isDownloadingPdf ? 'PDF Hazırlanıyor...' : 'PDF Rapor İndir'}
            </button>
          </div>
          <div className='row g-5 mb-5'>
            <div className='col-md-4'>
              <div className='card h-100 border-0 bg-light-primary'>
                <div className='card-body'>
                  <div className='text-muted fw-semibold mb-2'>Genel Ortalama</div>
                  <div className='fs-1 fw-bold text-primary'>{formatScore(reportSummary.overallAverage)}</div>
                  <div className='text-muted small'>Likert ve derecelendirme soruları üzerinden hesaplanır.</div>
                </div>
              </div>
            </div>
            <div className='col-md-4'>
              <div className='card h-100 border-0 bg-light-success'>
                <div className='card-body'>
                  <div className='text-muted fw-semibold mb-2'>Frekans Kalemi</div>
                  <div className='fs-1 fw-bold text-success'>{reportSummary.likertItems.length}</div>
                  <div className='text-muted small'>Likert/matris soru kırılımları rapora dahil edilir.</div>
                </div>
              </div>
            </div>
            <div className='col-md-4'>
              <div className='card h-100 border-0 bg-light-info'>
                <div className='card-body'>
                  <div className='text-muted fw-semibold mb-2'>Açık Uçlu Soru</div>
                  <div className='fs-1 fw-bold text-info'>{reportSummary.openEndedItems.length}</div>
                  <div className='text-muted small'>{openEndedAnswerCount} açık uçlu yanıt tam metin olarak listelenir.</div>
                </div>
              </div>
            </div>
          </div>
          {(reportSummary.strongestItems.length > 0 || reportSummary.improvementItems.length > 0) && (
            <div className='row g-5 mb-5'>
              <div className='col-lg-6'>
                <div className='card h-100'>
                  <div className='card-header'>
                    <h5 className='card-title mb-0'>En Güçlü Alanlar</h5>
                  </div>
                  <div className='card-body'>
                    {reportSummary.strongestItems.map((item) => (
                      <div key={item.key} className='d-flex justify-content-between gap-3 border-bottom py-2'>
                        <span>{getQuestionTitle(item)}</span>
                        <strong>{formatScore(item.normalizedAverage)}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className='col-lg-6'>
                <div className='card h-100'>
                  <div className='card-header'>
                    <h5 className='card-title mb-0'>Gelişime Açık Alanlar</h5>
                  </div>
                  <div className='card-body'>
                    {reportSummary.improvementItems.map((item) => (
                      <div key={item.key} className='d-flex justify-content-between gap-3 border-bottom py-2'>
                        <span>{getQuestionTitle(item)}</span>
                        <strong>{formatScore(item.normalizedAverage)}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
          {aiAnalysis && (
            <div className='card mb-5 border-0 bg-light-warning'>
              <div className='card-body'>
                <h5 className='mb-3'>Yapay Zeka Yorumu</h5>
                <div
                  dangerouslySetInnerHTML={{
                    __html: convertAnalysisToHtml(aiAnalysis),
                  }}
                />
              </div>
            </div>
          )}
          {reportSummary.likertItems.length > 0 && (
            <div className='card mb-5'>
              <div className='card-header'>
                <h5 className='card-title mb-0'>Likert Soru Frekansları</h5>
              </div>
              <div className='card-body'>
                <div className='table-responsive'>
                  <table className='table table-sm table-row-bordered align-middle'>
                    <thead>
                      <tr>
                        <th>Soru</th>
                        <th className='text-end'>Cevap</th>
                        <th className='text-end'>Ortalama</th>
                        <th>Dağılım</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportSummary.likertItems.map((item) => (
                        <tr key={item.key}>
                          <td style={{minWidth: 260}}>{getQuestionTitle(item)}</td>
                          <td className='text-end'>{item.answerCount}</td>
                          <td className='text-end'>{formatScore(item.normalizedAverage)}</td>
                          <td>
                            <div className='d-flex flex-column gap-1'>
                              {item.options.map((option) => (
                                <div key={option.value} className='d-flex justify-content-between gap-3 small'>
                                  <span>{option.label}</span>
                                  <span className='text-nowrap'>
                                    {option.count} ({formatPercent(option.percentage)})
                                  </span>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
          {reportSummary.openEndedItems.length > 0 && (
            <div className='card mb-5'>
              <div className='card-header align-items-center'>
                <h5 className='card-title mb-0'>Açık Uçlu Yanıtlar</h5>
                <div className='card-toolbar'>
                  <button
                    className='btn btn-sm btn-light-secondary'
                    onClick={downloadOpenEndedCsv}
                    disabled={openEndedAnswerCount === 0}
                  >
                    CSV İndir
                  </button>
                </div>
              </div>
              <div className='card-body'>
                {reportSummary.openEndedItems.map((item) => {
                  const answers = getOpenEndedAnswers(item)

                  return (
                    <div key={item.key} className='mb-8'>
                      <div className='d-flex justify-content-between flex-wrap gap-3 mb-3'>
                        <div>
                          <h6 className='fw-bold mb-1'>{getQuestionTitle(item)}</h6>
                          <div className='text-muted small'>
                            Yanıt sayısı: {answers.length}/{item.totalResponseCount}
                          </div>
                        </div>
                      </div>
                      {answers.length === 0 ? (
                        <div className='border rounded p-4 text-muted'>Bu soru için açık uçlu yanıt bulunmuyor.</div>
                      ) : (
                        answers.map((answer, index) => (
                          <div
                            key={`${item.key}-${index}`}
                            className='border rounded p-4 mb-3 bg-light'
                            style={{whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}
                          >
                            <div className='text-muted small fw-semibold mb-2'>Yanıt {index + 1}</div>
                            <div>{answer}</div>
                          </div>
                        ))
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}
          <SurveyGaugeChart surveyJson={survey} data={surveyResults} />
          {showMatrixCharts && (
            <MatrixQuestionChartSwitcher surveyJson={survey} surveyData={surveyResults} />
          )}
          <SurveyResultsComponent surveyJson={survey} surveyData={surveyResults} />
          <SurveyTableComponent surveyJson={survey} surveyData={surveyResults} />
        </>
      )}
    </>
  )
}

export default SurveyResultGeneral
